/**
 * The plan ladder, in one place.
 *
 * There were three copies of this: the pricing page, the onboarding flow and
 * the server. The onboarding copy had drifted to $9 / $29 / $59 / $99 with
 * yearly prices — four tiers at numbers the subscribe route cannot charge,
 * shown to every seller during install, which is the worst possible place for
 * a wrong price. It had drifted because nothing connected it to anything.
 *
 * So the two front-end screens read this and the backend test asserts it
 * against billing.js. A price can still be changed in the wrong place, but it
 * can no longer be changed in one place and stay wrong in another.
 *
 * ── Where the numbers come from ──
 *
 * The allowances are DPL's, matched tier for tier, at a lower price:
 *
 *  
 * The first four were read off their Shopify App Store listing. The fifth is
 * not published there — it came from an earlier note and the figures are
 * unverified, so treat 30,000 / 15,000 as the one line here that has not been
 * checked against a live source.
 */

/** What every plan carries, stated once so "in all plans" cannot drift. */
export const SHOPIFY_COMMON = [
  { text: 'AI help optimising listings',   ok: true },
  { text: 'AI help creating rules',        ok: true },
  { text: 'Email, chat & ticket support',  ok: true },
]

/** Support above the $29 tier: the three channels, plus time with a person. */
export const SUPPORT_PLUS = [
  { text: 'Onboarding meetings & seller groups', ok: true },
]

/**
 * 3 days on every plan.
 *
 * Not a ladder, because a ladder was not true of any of them: nothing prunes
 * sync_logs, so every tier keeps everything and the figures differed only on
 * the card.
 */
export const ACTIVITY_LOG = { text: 'Activity log (3 days)', ok: true }

/**
 * Shopify as source is one-way: Shopify → eBay.
 *
 * Said per bullet rather than only in the blurb above the grid. A card is
 * what gets screenshotted and compared against a competitor's.
 */
export const SHOPIFY_PLANS = [
  {
    id: 'starter', name: 'Starter', monthly: 19, products: 2500, orders: 100,
    desc: 'For a small catalogue finding its feet on eBay.',
    features: [
      { text: '2,500 products synced',        ok: true  },
      { text: '100 eBay orders a month',      ok: true  },
      { text: 'Price sync (Shopify → eBay)',  ok: true  },
      { text: 'Stock sync (Shopify → eBay)',  ok: true  },
      ACTIVITY_LOG,
      ...SHOPIFY_COMMON,
      { text: 'Auto sync schedule',           ok: false },
    ],
  },
  {
    id: 'standard', name: 'Standard', monthly: 29, products: 5000, orders: 4000,
    desc: 'For a catalogue that has outgrown the entry tier.',
    features: [
      { text: '5,000 products synced',        ok: true  },
      { text: '4,000 eBay orders a month',    ok: true  },
      { text: 'Auto sync schedule',           ok: true  },
      { text: 'Price sync (Shopify → eBay)',  ok: true  },
      { text: 'Stock sync (Shopify → eBay)',  ok: true  },
      ACTIVITY_LOG,
      ...SHOPIFY_COMMON,
    ],
  },
  {
    id: 'growth', name: 'Growth', monthly: 59, products: 10000, orders: 7000, popular: true,
    desc: 'For a shop selling steadily on eBay.',
    features: [
      { text: '10,000 products synced',       ok: true  },
      { text: '7,000 eBay orders a month',    ok: true  },
      { text: 'Auto sync schedule',           ok: true  },
      { text: 'Price sync (Shopify → eBay)',  ok: true  },
      { text: 'Stock sync (Shopify → eBay)',  ok: true  },
      { text: 'Sync manual eBay stock changes', ok: true },
      ACTIVITY_LOG,
      ...SHOPIFY_COMMON,
      ...SUPPORT_PLUS,
    ],
  },
  {
    id: 'pro', name: 'Pro', monthly: 199, products: 20000, orders: 10000,
    desc: 'For a large catalogue and real order volume.',
    features: [
      { text: '20,000 products synced',       ok: true  },
      { text: '10,000 eBay orders a month',   ok: true  },
      { text: 'Auto sync schedule',           ok: true  },
      { text: 'Price sync (Shopify → eBay)',  ok: true  },
      { text: 'Stock sync (Shopify → eBay)',  ok: true  },
      { text: 'Sync manual eBay stock changes', ok: true },
      { text: 'Priority sync support',        ok: true  },
      ACTIVITY_LOG,
      ...SHOPIFY_COMMON,
      ...SUPPORT_PLUS,
    ],
  },
  {
    id: 'enterprise', name: 'Enterprise', monthly: 349, products: 30000, orders: 15000,
    enterprise: true,
    desc: 'For the largest catalogues, with support to match.',
    features: [
      { text: '30,000 products synced',       ok: true  },
      { text: '15,000 eBay orders a month',   ok: true  },
      { text: 'Auto sync schedule',           ok: true  },
      { text: 'Price sync (Shopify → eBay)',  ok: true  },
      { text: 'Stock sync (Shopify → eBay)',  ok: true  },
      { text: 'Sync manual eBay stock changes', ok: true },
      { text: 'Priority sync support',        ok: true  },
      ACTIVITY_LOG,
      ...SHOPIFY_COMMON,
      ...SUPPORT_PLUS,
    ],
  },
]

/**
 * eBay as source: a one-time import, and optionally a plan on top.
 *
 *   import only       10c a listing, charged once. The listings become
 *                     Shopify products and nothing is kept in step after —
 *                     the right purchase for a catalogue moved in once.
 *   import + a plan   the same fee, plus a monthly tier. Every sync runs.
 *
 * The rate here is a preview. The server holds it too and recomputes every
 * charge from the quantity, because a price the browser can send is a price
 * the browser can choose.
 */
export const EBAY_SETUP_PER_PRODUCT = 0.10
export const EBAY_MIN_PRODUCTS = 100
export const EBAY_MAX_PRODUCTS = 200000

/**
 * The monthly tier a catalogue of this size needs — the smallest whose
 * ceiling covers it, which is the rule the server applies too. Quoting a
 * bigger one because it earns more is what a seller finds out later.
 */
export function tierFor(products) {
  return SHOPIFY_PLANS.find(p => p.products >= products)
      || SHOPIFY_PLANS[SHOPIFY_PLANS.length - 1]
}

/** The one-time import fee for this many listings, clamped to the bounds. */
export function setupFee(n) {
  const q = Math.min(EBAY_MAX_PRODUCTS,
                     Math.max(EBAY_MIN_PRODUCTS, Math.floor(Number(n) || 0)))
  return Math.round(q * EBAY_SETUP_PER_PRODUCT * 100) / 100
}
